import React, { useEffect } from "react";
import MetaTags from "@components/shared/metaTags";
import { GQLCategories, GQLUrls } from "@gql/types/gql";
import {
    getMainTitlesNavigationFromBody,
    replaceDkis,
    resolveURL,
    titleDecoder,
} from "@lib/sharedUtils";
import { useQuery } from "@tanstack/react-query";
import SideWidget from "@components/shared/sideWidget";
import Breadcrumb from "@components/shared/breadcrumb";
import ContentTab from "@components/shared/contentTab";
import Text from "@components/shared/text";
import Vendor from "@components/vendor";
import { getUrl } from "@helpers/switchLanguage";
import { translate } from "@helpers/translationObjectFormatter";
import {
    ICustomDomain,
    IReviewOfferPageProps,
    TranslationsObject,
} from "@genericTypes/sharedTypes";
import { Languages } from "@genericTypes/sharedTypes";
import FreeQuotePopup from "@components/shared/freeQuotePopup";
import { selectedCategoryAtom } from "@store/formStore";
import { useAtom } from "jotai";
import { SelectedCategory } from "@genericTypes/form-store-types";
import Rating from "@components/rating";
import { Session } from "src/api/session";
import Share from "../share/Share";
import AuthorInfo from "@components/blog/authorInfo";
export interface VendorsToCompare {
    slug: string;
    label: string;
    logo: string;
    category: string;
}

export default function MainVendorPage({
    title,
    url,
}: {
    title: string;
    url: string;
}) {
    const { data: translations } = useQuery<TranslationsObject>([
        "translations",
    ]);
    const session = useQuery<Session>({ queryKey: ["session"] });
    const { data: props } = useQuery<IReviewOfferPageProps>({
        queryKey: ["reviewPageProps"],
    });

    const { category, offers: domainOffers, dkis, offer } = props ?? {};

    const arrTab = [
        {
            label: translate(translations, "several_general_reviewWord"),
            slug: "review",
            link: `${resolveURL(offer?.url as GQLUrls)}`,
        },

        {
            label: translate(translations, "several_general_compareButton"),
            slug: "compare",
            link: `${resolveURL(offer?.url as GQLUrls)}/compare`,
        },
    ];

    const [, setSelectedCategory] = useAtom(selectedCategoryAtom);
    useEffect(() => {
        if (category) {
            setSelectedCategory(category as SelectedCategory);
        }
    }, [category]);

    const vendorsToCompare: VendorsToCompare[] = domainOffers
        ?.filter((item) => item?.slug !== offer?.slug)
        .map((offer) => {
            return {
                slug: offer?.slug as string,
                label: offer?.featuresListTitle as string,
                logo: offer?.logo?.fullPath,
                category: (category as GQLCategories)?.slug,
            } as VendorsToCompare;
        }) as VendorsToCompare[];
    let arr;
    if (category && (category?.label || category?.name)) {
        arr = [
            {
                label: translate(
                    translations,
                    "several_general_breadcrumb_home",
                ),
                link: "/",
                isActive: true,
                showOnMobile: true,
            },

            {
                label: `${
                    (category?.label as string) ?? (category?.name as string)
                }`,
                link: `${resolveURL(category?.url as GQLUrls)}`,
                isActive: true,
                showOnMobile: true,
            },
            {
                label: replaceDkis(offer?.url?.url as string, dkis),
                link: `${resolveURL(offer?.url as GQLUrls)}`,
                isActive: false,
                showOnMobile: false,
            },
        ];
    } else {
        arr = [
            {
                label: translate(
                    translations,
                    "several_general_breadcrumb_home",
                ),
                link: "/",
                isActive: true,
                showOnMobile: true,
            },
            {
                label: replaceDkis(offer?.url?.url as string, dkis),
                link: `${resolveURL(offer?.url as GQLUrls)}`,
                isActive: false,
                showOnMobile: false,
            },
        ];
    }

    const myList = [
        {
            title: translate(translations, "several_general_contents"),
            slug: "contents",
            content:
                (offer?.faqs?.length as number) > 0
                    ? [
                          ...(getMainTitlesNavigationFromBody(
                              offer?.body as string,
                          ) as []),
                          {
                              title: "Frequently Asked Questions (FAQs)",
                              link: "#faq",
                          },
                      ]
                    : (getMainTitlesNavigationFromBody(
                          offer?.body as string,
                      ) as []),
        },
    ];

    const { data: domainInfo } = useQuery<ICustomDomain, Error>({
        queryKey: ["domain"],
    });
    const getKeyWords = () => {
        for (let i = 0; i < (offer?.metatags?.length ?? 0); i++) {
            if (
                offer?.metatags![i]?.key === "keywords" &&
                offer?.metatags[i]?.value
            ) {
                return offer?.metatags[i]?.value;
            }
        }
        domainInfo?.domain?.metatags?.forEach((metaTag) => {
            if (metaTag?.key === "keywords" && metaTag.value) {
                return metaTag.value;
            }
        });

        return "vpn , auto warranty";
    };
    const getDescription = (fallback: string) => {
        for (let i = 0; i < (offer?.metatags?.length ?? 0); i++) {
            if (
                offer?.metatags![i]?.key === "description" &&
                offer?.metatags[i]?.value
            ) {
                return offer?.metatags[i]?.value;
            }
        }
        if (offer?.overview) {
            return offer.overview.replace(/<[^>]+>/g, "").substring(0, 160);
        }
        domainInfo?.domain?.metatags?.forEach((metaTag) => {
            if (metaTag?.key === "description" && metaTag.value) {
                return metaTag.value;
            }
        });
        return fallback;
    };

    return (
        <>
            <MetaTags
                title={`${
                    title ? title : (offer?.title as string)
                } | Several.com`}
                image={
                    (offer?.mainImage?.fullPath as string) ??
                    `${getUrl(
                        process.env.NEXT_PUBLIC_DEFAULT_LANG as Languages,
                        "/placeholder_several.jpg",
                    )}`
                }
                keyWords={getKeyWords() as string}
                description={
                    getDescription(
                        translate(
                            translations,
                            "several_general_meta_description",
                        ),
                    ) as string
                }
                url={url}
            />
            <div>
                <div className="max-w-7xl mx-auto px-5">
                    <Breadcrumb arr={arr} />
                    <div className="flex justify-between items-baseline mt-4 flex-col lg:flex-row  lg:ml-0">
                        <Text
                            className="lg:text-3xl lg:leading-9 font-bold  max-w-[744px]"
                            type="mainTitle"
                            itemProp="name"
                        >
                            {titleDecoder(offer?.title ?? "", {
                                category: category
                                    ? ((category.label ??
                                          category.name) as string)
                                    : "",
                                region:
                                    (session?.data?.visitDetails
                                        ?.region as string) ?? "",
                            })}
                        </Text>
                    </div>
                    {offer?.writerUser?.nickname && (
                        <AuthorInfo mainInfo={offer} offer />
                    )}
                    {offer?.score && (
                        <div className="mt-3 sm:hidden">
                            <Rating value={offer?.score} />
                        </div>
                    )}
                    <Share
                        contentToBeSharedUrl={resolveURL(offer?.url as GQLUrls)}
                        secondStyle
                    />

                    <div className="mt-5">
                        <ContentTab contentTab={arrTab} />
                    </div>
                    <div className=" flex flex-col-reverse lg:flex-row lg:items-start items-center mb-16  lg:my-16 ">
                        <SideWidget
                            myList={myList}
                            vendor={offer}
                            VendorsToCompare={vendorsToCompare}
                            showSideBySideComparison
                            contentToBeSharedUrl={`${resolveURL(
                                offer?.url as GQLUrls,
                            )}`}
                            category={category as GQLCategories}
                        />
                        {category && (
                            <>
                                <Vendor
                                    key={offer?.slug}
                                    vendor={offer}
                                    categoryId={category.id!}
                                    listOfContentsMobile={myList[0].content}
                                    VendorsToCompare={vendorsToCompare}
                                    category={category}
                                />
                                <FreeQuotePopup category={category} />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
